import { useState, useEffect, useCallback } from "react";
import {
  Users, Loader2, RefreshCw, Eye, MessageSquarePlus, CheckCircle2,
  Clock, X, ChevronRight, MapPin, Phone, Mail, School, Calendar,
  AlertCircle, Edit3,
  Search,
} from "lucide-react";
import AdminHeader from "@/components/common/AdminHeader";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { getLeads, createLeadRemark, getLeadRemarks, updateLead } from "@/services/leads";
import { getStates } from "@/services/masters";
import toast from "react-hot-toast";
import Pagination from "@/components/common/Pagination";

interface Lead {
  id: number;
  Full_Name: string;
  Email: string;
  Phone_Number: string;
  School_Name: string;
  City: string;
  Timestamp: string;
  Created_At: string;
  Lead_Status: boolean;
  Lead_Status_Date: string | null;
  Lead_Status_Remarks: string | null;
  Updated_By: string | null;
}

interface Remark {
  id: number;
  Remarks: string;
  Created_At: string;
  Created_By: string;
  Requested_user: number;
}

interface State {
  id: number;
  Name: string;
  State_Code: string;
}

/* ──────────────────────────── Remark Modal ──────────────────────────── */
function RemarkModal({
  lead,
  onClose,
  onSubmit,
  submitting,
}: {
  lead: Lead;
  onClose: () => void;
  onSubmit: (remark: string) => void;
  submitting: boolean;
}) {
  const [text, setText] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MessageSquarePlus className="w-5 h-5 text-brand-500" />
            <h3 className="font-bold text-gray-900">Add Remark</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="font-semibold text-gray-900 text-sm">{lead.Full_Name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{lead.Email} · {lead.Phone_Number}</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Remark</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Enter your remark here..."
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none"
            />
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(text)}
            disabled={!text.trim() || submitting}
            className="px-4 py-2 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Submit Remark
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── View Remarks Modal ──────────────────────────── */
function ViewRemarksModal({
  lead,
  onClose,
}: {
  lead: Lead;
  onClose: () => void;
}) {
  const [remarks, setRemarks] = useState<Remark[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchRemarks = async () => {
      try {
        setLoading(true);

        const response = await getLeadRemarks(lead.id, currentPage);

        const data = response?.data as any;

        if (!isMounted) return;

        if (Array.isArray(data)) {
          setRemarks(data);
          setTotalPages(1);
          setTotalCount(data.length);
        } else {
          setRemarks(Array.isArray(data?.results) ? data.results : []);
          setTotalPages(data?.total_pages ?? 1);
          setTotalCount(data?.count ?? 0);
        }
      } catch (error) {
        if (isMounted) {
          setRemarks([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRemarks();

    return () => {
      isMounted = false;
    };
  }, [lead.id, currentPage]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-brand-500" />
            <h3 className="font-bold text-gray-900">Remarks</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 pt-4 pb-2">
          <div className="bg-gray-50 rounded-xl p-3 mb-4">
            <p className="font-semibold text-gray-900 text-sm">{lead.Full_Name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{lead.Email} · {lead.Phone_Number}</p>
          </div>
        </div>
        <div className="px-6 pb-5">
  <div className="max-h-72 overflow-y-auto space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-brand-500 mr-2" />
              <p className="text-sm text-gray-500">Loading remarks…</p>
            </div>
          ) : remarks?.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <MessageSquarePlus className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No remarks yet.</p>
            </div>
          ) : (
            remarks && remarks.map((r) => (
              <div key={r.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-sm text-gray-800">{r.Remarks}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-semibold text-brand-500">{r.Created_By}</span>
                  <span className="text-xs text-gray-400">{new Date(r.Created_At).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          )}
           {totalPages > 1 && (
    <div className="mt-4">
      <Pagination
        itemsPerPage={20}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />
    </div>
  )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── Update Status Modal ──────────────────────────── */
function UpdateStatusModal({
  lead,
  states,
  onClose,
  onSubmit,
  submitting,
}: {
  lead: Lead;
  states: State[];
  onClose: () => void;
  onSubmit: (data: { Lead_Status_Date: string; Lead_Status: number; Lead_Status_Remarks: string; State: string }) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState({
    Lead_Status_Date: lead.Lead_Status_Date ?? new Date().toISOString().split("T")[0],
    Lead_Status: lead.Lead_Status ? 1 : 0,
    Lead_Status_Remarks: lead.Lead_Status_Remarks ?? "",
    State: "",
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-gray-900">Update Lead Status</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="font-semibold text-gray-900 text-sm">{lead.Full_Name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{lead.Email} · {lead.City}</p>
          </div>

          {/* Lead Status toggle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Lead Status</label>
            <div className="flex gap-3">
              <button
                onClick={() => setForm(f => ({ ...f, Lead_Status: 0 }))}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${form.Lead_Status === 0
                    ? "bg-red-50 border-red-300 text-red-700"
                    : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                  }`}
              >
                Pending
              </button>
              <button
                onClick={() => setForm(f => ({ ...f, Lead_Status: 1 }))}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${form.Lead_Status === 1
                    ? "bg-green-50 border-green-300 text-green-700"
                    : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                  }`}
              >
                Completed
              </button>
            </div>
          </div>

          {/* State dropdown */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">State</label>
            <select
              value={form.State}
              onChange={(e) => setForm(f => ({ ...f, State: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
            >
              <option value="">Select state…</option>
              {states.map((s) => (
                <option key={s.id} value={s.Name}>{s.Name}</option>
              ))}
            </select>
          </div>

          {/* Status date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status Date</label>
            <input
              type="date"
              value={form.Lead_Status_Date}
              onChange={(e) => setForm(f => ({ ...f, Lead_Status_Date: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status Remarks</label>
            <textarea
              value={form.Lead_Status_Remarks}
              onChange={(e) => setForm(f => ({ ...f, Lead_Status_Remarks: e.target.value }))}
              rows={3}
              placeholder="Enter status remarks..."
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
            />
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(form)}
            disabled={!form.Lead_Status_Date || !form.State || submitting}
            className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Update Status
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── Main Page ──────────────────────────── */
export default function LeadsPage() {
  const { canView, canCreate, canUpdate, loading: permLoading } = useScreenPermission("leads");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [remarkTarget, setRemarkTarget] = useState<Lead | null>(null);
  const [viewTarget, setViewTarget] = useState<Lead | null>(null);
  const [updateTarget, setUpdateTarget] = useState<Lead | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "completed">("all");
  const [searchLoading, setSearchLoading] = useState(false);

  const load = useCallback(
    async (quiet = false, page = currentPage, searchValue = search) => {
      if (!quiet) {
        if (searchValue.trim()) {
          setSearchLoading(true);
        } else {
          setLoading(true);
        }
      }

      try {
        const [leadsRes, statesRes] = await Promise.all([
          getLeads(searchValue, page),
          getStates(),
        ]);

        const leadsData = leadsRes?.data as any;

        if (leadsData) {
          setLeads(
            Array.isArray(leadsData)
              ? leadsData
              : leadsData.results ?? []
          );

          setTotalPages(
            Array.isArray(leadsData)
              ? 1
              : leadsData.total_pages ?? 1
          );

          setTotalCount(
            Array.isArray(leadsData)
              ? leadsData.length
              : leadsData.count ?? 0
          );
        } else {
          setLeads([]);
          setTotalPages(1);
          setTotalCount(0);
        }

        const statesData =
          (statesRes.data as any)?.results ??
          (statesRes.data as any) ??
          [];

        setStates(
          Array.isArray(statesData) ? statesData : []
        );
      } catch (error) {
        console.error("Error loading leads:", error);
        toast.error("Failed to load leads");
      } finally {
        setLoading(false);
        setSearchLoading(false);
        setRefreshing(false);
      }
    },
    [currentPage, search]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      load(false, currentPage, search);
    }, search.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [currentPage, search, load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load(true, currentPage);
  };
  const handleAddRemark = async (remark: string) => {
    if (!remarkTarget) return;
    setSubmitting(true);
    try {
      await createLeadRemark({ Requested_user: remarkTarget.id, Remarks: remark });
      toast.success("Remark added successfully");
      setRemarkTarget(null);
    } catch {
      toast.error("Failed to add remark");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (data: {
    Lead_Status_Date: string;
    Lead_Status: number;
    Lead_Status_Remarks: string;
    State: string;
  }) => {
    if (!updateTarget) return;
    setSubmitting(true);
    try {
      await updateLead(updateTarget.id, data);
      toast.success("Lead status updated");
      setUpdateTarget(null);
      load(true, currentPage);
    } catch {
      toast.error("Failed to update lead status");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = leads.filter((l) => {
    if (filter === "pending") return !l.Lead_Status;
    if (filter === "completed") return l.Lead_Status;
    return true;
  });

  const totalLeads = leads.length;
  const completed = leads.filter((l) => l.Lead_Status).length;
  const pending = leads.filter((l) => !l.Lead_Status).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <main className="max-w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900">Leads</h1>
              <p className="text-gray-500 text-sm">Website enquiry leads & follow-up management</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {permLoading || loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-brand-500 mr-2" />
            <p className="text-gray-500">Loading…</p>
          </div>
        ) : !canView ? (
          <div className="flex items-center justify-center py-32">
            <p className="text-gray-500">You don't have permission to view leads.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-center">
                <p className="text-3xl sm:text-4xl font-extrabold text-gray-800">{totalLeads}</p>
                <p className="text-xs text-gray-500 mt-1 font-medium">Total Leads</p>
              </div>
              <div className="bg-green-50 rounded-xl border border-green-100 shadow-sm p-5 text-center">
                <p className="text-3xl sm:text-4xl font-extrabold text-green-700">{completed}</p>
                <p className="text-xs text-gray-500 mt-1 font-medium">Completed</p>
              </div>
              <div className="bg-amber-50 rounded-xl border border-amber-100 shadow-sm p-5 text-center">
                <p className="text-3xl sm:text-4xl font-extrabold text-amber-700">{pending}</p>
                <p className="text-xs text-gray-500 mt-1 font-medium">Pending</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search leads..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            </div>
            {totalPages > 1 && (
              <Pagination
                itemsPerPage={20}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={(page) => {
                  setCurrentPage(page);
                }}
              />
            )}

            {/* Filter tabs */}
            <div className="flex items-center gap-2">
              {(["all", "pending", "completed"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === f
                      ? "bg-gray-900 text-white"
                      : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {f === "all" ? "All" : f === "pending" ? "Pending" : "Completed"}
                </button>
              ))}
              <span className="ml-auto text-xs text-gray-400 font-medium">{filtered.length} records</span>
            </div>

            {/* Leads list */}
            {filtered.length === 0 ? (
              <div className="text-center py-24 text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No leads found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4 px-5 py-4">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-extrabold text-brand-700">
                          {lead.Full_Name?.[0]?.toUpperCase()}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-gray-900">{lead.Full_Name}</p>
                          {lead.Lead_Status ? (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Completed
                            </span>
                          ) : (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.Email}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.Phone_Number}</span>
                          <span className="flex items-center gap-1"><School className="w-3 h-3" />{lead.School_Name}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.City}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(lead.Created_At).toLocaleDateString()}</span>
                        </div>
                        {lead.Lead_Status_Remarks && (
                          <p className="mt-1.5 text-xs text-gray-400 italic truncate max-w-md">
                            Status note: {lead.Lead_Status_Remarks}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* View remarks */}
                        <button
                          onClick={() => setViewTarget(lead)}
                          title="View Remarks"
                          className="p-2 rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Add remark — only if pending and canCreate */}
                        {!lead.Lead_Status && canCreate && (
                          <button
                            onClick={() => setRemarkTarget(lead)}
                            title="Add Remark"
                            className="p-2 rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors"
                          >
                            <MessageSquarePlus className="w-4 h-4" />
                          </button>
                        )}

                        {/* Update status — canUpdate */}
                        {canUpdate && (
                          <button
                            onClick={() => setUpdateTarget(lead)}
                            title="Update Status"
                            className="p-2 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Status date footer */}
                    {lead.Lead_Status && lead.Lead_Status_Date && (
                      <div className="px-5 py-2 bg-green-50 border-t border-green-100 flex items-center gap-1.5 text-xs text-green-700">
                        <CheckCircle2 className="w-3 h-3" />
                        Completed on {new Date(lead.Lead_Status_Date).toLocaleDateString()}
                        {lead.Updated_By && <> · by <span className="font-semibold">{lead.Updated_By}</span></>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      {remarkTarget && (
        <RemarkModal
          lead={remarkTarget}
          onClose={() => setRemarkTarget(null)}
          onSubmit={handleAddRemark}
          submitting={submitting}
        />
      )}
      {viewTarget && (
        <ViewRemarksModal
          lead={viewTarget}
          onClose={() => setViewTarget(null)}
        />
      )}
      {updateTarget && (
        <UpdateStatusModal
          lead={updateTarget}
          states={states}
          onClose={() => setUpdateTarget(null)}
          onSubmit={handleUpdateStatus}
          submitting={submitting}
        />
      )}
    </div>
  );
}
